import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import playIcon from "../../images/playIcon.svg";

export const AthleteCoachedStrengthWorkoutExercise = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openAcc, secOpenAcc] = useState(null);
  const { weekId, day, exercise } = location.state || {};

  const exerciseData = exercise;
  const ex = exerciseData?.exercise_id || null;

  const [showVideo, setShowVideo] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");

  const [sets, setSets] = useState([]);
  const [oneRepMax, setOneRepMax] = useState(exerciseData?.one_rm || "");

  const handleOpenAccordion = (index) => {
    secOpenAcc((prev) => (prev === index ? null : index));
  };
  // ------------------------
  // Helpers
  // ------------------------


  const convertToEmbed = (url) => {
    const id = extractYouTubeID(url);
    return id ? `https://www.youtube.com/embed/${id}` : "";
  };

  const extractYouTubeID = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };
  
  const getThumbnail = (url) => {
    const id = extractYouTubeID(url);
    return id
      ? `https://img.youtube.com/vi/${id}/hqdefault.jpg`
      : "https://via.placeholder.com/640x360?text=No+Thumbnail";
  };
  // ------------------------
  // Compute weight for sets
  // ------------------------
  const computeSets = (baseSets, oneRM) => {
    if (!baseSets || !Array.isArray(baseSets)) return [];

    return baseSets.map((s) => {
      if (s.unit === "%") {
        if (!oneRM || isNaN(oneRM)) return { ...s, weight: 0 };
        const computed = Math.round((s.weight * oneRM) / 100 / 5) * 5; // nearest 5
        return { ...s, weight: computed, unit: "lbs" };
      }
      return s; // lbs, time, hide → unchanged
    });
  };

  // ------------------------
  // Load data on mount
  // ------------------------
  useEffect(() => {
    if (!exerciseData) return;
    setEmbedUrl(convertToEmbed(ex.video_url));
    setSets(computeSets(exerciseData.sets, oneRepMax));
  }, [exerciseData, oneRepMax]);

  // ------------------------
  // Handle one_rm changes
  // ------------------------
  const handleOneRmChange = (e) => {
    const val = parseFloat(e.target.value);
    setOneRepMax(val || "");
    setSets(computeSets(exerciseData.sets, val));
  };

  const handleBack = () => navigate(-1);

  const [showFullDesc, setShowFullDesc] = useState(false);

  if (!exerciseData || !ex) {
    return (
      <AthleteLayout>
        <p className="text-black text-center py-10">No exercise data found</p>
      </AthleteLayout>
    );
  }

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div>
            <AthleteHeader />
          </div>

          {/* HEADER */}
          <div className="w-full max-w-2xl mx-auto pt-3 px-3 xs:px-5">
            <div className="pb-4 border-b border-black/30">
              <div className="bg-[#f5f4f4] rounded-xl p-3 flex items-center gap-5">
                <span
                  className="text-black cursor-pointer"
                  onClick={handleBack}
                >
                  <BackArrow />
                </span>

                <div className="relative text-start">
                  <p className="text-black text-base xs:text-lg font-semibold">
                    Strength Workout
                  </p>

                  {/* <p className="text-lightSmock text-sm mt-1">{day?.date}</p> */}
                </div>
              </div>
            </div>
          </div>

          {/* VIDEO + TITLE + DESCRIPTION */}
          <div className="max-w-2xl mx-auto px-3 xs:px-5 py-4">
            <div>
              <div className="relative h-[210px] mb-4">
                <img
                  src={getThumbnail(ex.video_url)}
                  className="w-full h-full object-cover"
                  alt=""
                />

                {ex.video_url && (
                  <img
                    src={playIcon}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowVideo(true)}
                  />
                )}

                <div className="absolute bottom-4 left-4">
                  <p className="text-white text-lg xs:text-xl font-light uppercase">
                    {ex.title}
                  </p>
                </div>
              </div>

              <p className="text-black font-medium mt-2 whitespace-pre-line">
                {showFullDesc ? ex.description : ex.description?.slice(0, 180)}
              </p>
              {ex.description?.length > 180 && (
                <button
                  className="text-blue-400 underline font-medium mt-2 text-sm"
                  onClick={() => setShowFullDesc(!showFullDesc)}
                >
                  {showFullDesc ? "Show Less" : "Show More"}
                </button>
              )}

              {/* ONE RM INPUT */}
              <div className="w-full mt-3">
                <div className="w-full relative">
                  <label className="text-grayish font-light text-sm pr-3 border-r border-grayish absolute top-3 left-3">
                    Enter 1RM
                  </label>
                  <input
                    type="number"
                    value={oneRepMax || ""}
                    onChange={handleOneRmChange}
                    placeholder="Enter 1RM to update % sets"
                    className=" w-full
                    border border-grayish rounded-2xl bg-transparent text-black font-light text-base leading-none py-3 pl-28 placeholder:text-grayish placeholder:uppercase"
                  />
                </div>
                {exerciseData?.one_rm && (
                  <div className="my-3">
                    <p className="text-grayish font-medium text-sm mt-1">
                      Original 1RM:{" "}
                      <span className="text-black font-bold">
                        {exerciseData.one_rm} lbs
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* label / header */}
          <div className="max-w-2xl mx-auto px-3 xs:px-5 mb-5">
            <div className="bg-black rounded-xl py-3 px-3 divide-x-2 divide-white/50 flex flex-row justify-between items-stretch text-center relative z-10">
              <div className="w-[15%] px-1.5 flex justify-center items-center">
                <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                  Sets
                </p>
              </div>
              <div className="w-[15%] px-1.5 flex justify-center items-center">
                <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                  Reps
                </p>
              </div>
              <div className="w-[28%] px-1 flex justify-center items-center">
                <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                  Load/Time
                </p>
              
              </div>
              <div className="w-[28%] px-1 flex justify-center items-center">
                <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                  goal
                </p>
              </div>
              <div className="w-[10%] px-1 flex justify-center items-center">
                <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                  Rest
                </p>
              </div>
              <div className="w-[6%] opacity-0">
                <div className="w-5 h-5 xs:w-6 xs:h-6"></div>
              </div>
            </div>
          </div>
          {/* SETS */}
          <div className="w-full max-w-2xl mx-auto px-3 xs:px-5">
            {sets.length === 0 ? (
              <p className="text-center text-gray-500">No sets available</p>
            ) : (
              sets.map((s, i) => (
                <div
                  key={s._id || i}
                  onClick={() => handleOpenAccordion(i)}
                  className="mb-5 p-3 xs:p-5 relative border border-black/30 rounded-2xl"
                >
                  <div className="relative flex flex-wrap items-stretch cursor-pointer">
                    <div className="max-xs:h-auto w-[14%] flex justify-center items-center border-r border-black">
                      <p className="text-black max-xs:text-[11px] font-light text-center px-1">
                        1
                      </p>
                    </div>

                    <div className="max-xs:h-auto w-[15%] flex justify-center items-center border-r border-black">
                      <p className="text-black max-xs:text-[11px] font-light text-center truncate px-1">
                        {s.reps}
                      </p>
                    </div>

                    <div className="max-xs:h-auto w-[28%] flex justify-center items-center border-r border-black">
                      <div className="px-1">
                        {s.unit !== "hide" && (
                          <p className="text-black max-xs:text-[11px] font-light text-center truncate">
                            {`${s.weight} ${s.unit}`}
                            {s.unit === "time" && s.time ? ` /  ${s.time}` : ""}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="max-xs:h-auto w-[28%] flex justify-center items-center gap-1 border-r border-black">
                      <div className="px-1">
                        {s.goal && (
                          <p className="text-black max-xs:text-[11px] font-light text-center truncate">
                            {/* {s.goal} {s.goalType || ""} */}
                            {s.goal.replace(/\s*\([^)]*\)/g, "")}
                          </p>
                        )}
                      </div>
                    </div>

                    {s.rest && (
                      <div className="max-xs:h-auto w-[10%] flex justify-center items-center">
                        {(() => {
                         // const minutes = s.rest.match(/\d+/)?.[0]; // extract number

                          return (
                            <p className="text-black max-xs:text-[11px] font-light text-center px-1">
                              {s.rest}
                            </p>
                          );
                        })()}
                      </div>
                    )}

                    <div className="w-[5%]">
                      <span
                        className={`transform transition-transform duration-300 cursor-pointer flex justify-center items-center bg-darkYellow p-1 text-black leading-none w-5 h-5 xs:w-6 xs:h-6 text-center`}
                      >
                        {/* <Plus /> */}
                        {openAcc === i ? <Minus /> : <Plus />}
                      </span>
                    </div>
                  </div>

                  {openAcc === i && s.notes && (
                    <div className="pt-3 mt-3 border-t border-darkYellow">
                     <p className="text-black font-light break-words">
                        Notes: {s.notes}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* VIDEO MODAL */}
          {showVideo && (
            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-3 z-50">
              <div className="relative bg-black w-[90%] max-w-lg">
                <button
                  className="absolute top-2 right-2 bg-gray-400 rounded-md p-2 text-white hover:bg-gray-300 transition"
                  onClick={() => setShowVideo(false)}
                >
                  Close
                </button>
                <iframe
                  width="100%"
                  height="315"
                  src={embedUrl}
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

const BackArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      d="M15 19l-7-7 7-7"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

export default AthleteCoachedStrengthWorkoutExercise;
